import { Drawer, DrawerContent, DrawerTitle } from '../ui/drawer';
import { Sheet, SheetContent, SheetTitle } from '../ui/sheet';
import { PanelTransactionActions } from '@/client/components/trade/panel-transaction-actions';
import { useTradeOverlaySlot } from '@/client/store/trade-overlay-store';
import { cn } from '@/client/utils';
import { isInsideTransactionStatus } from '@/client/utils/transaction-status-target';
import type { PropsWithChildren } from 'react';
import useMedia from 'react-use/lib/useMedia';

type DrawerSheetTitleProps = PropsWithChildren & {
  className?: string;
};

export const DrawerSheetTitle = ({ className, children }: DrawerSheetTitleProps) => {
  const isDesktop = useMedia('(min-width: 768px)');
  if (isDesktop) return <SheetTitle className={className}>{children}</SheetTitle>;
  return <DrawerTitle className={className}>{children}</DrawerTitle>;
};

type DrawerSheetProps = PropsWithChildren & {
  className?: string;
  side?: 'top' | 'bottom' | 'left' | 'right' | null | undefined;
  open?: boolean;
  setOpen?: (open: boolean) => void;
  disableOutside?: boolean;
  /**
   * The market whose trade UI this drawer hosts. On mobile, while open, the global
   * transaction toast hides for that market's records and the drawer shows their
   * recovery actions at the bottom instead. Records of other markets keep the toast.
   */
  transactionMarket?: string;
};
export const DrawerSheet = ({
  open,
  setOpen,
  children,
  className,
  side = 'right',
  disableOutside,
  transactionMarket,
}: DrawerSheetProps) => {
  const isDesktop = useMedia('(min-width: 768px)');
  const overlayOwner = useTradeOverlaySlot(!!open, transactionMarket);
  // The transaction toast floats above the drawer (outside its content). Tapping
  // it must not count as an outside interaction, or the trade panel closes.
  const onInteractOutside = (e: { target: EventTarget | null; preventDefault: () => void }) => {
    if (disableOutside || isInsideTransactionStatus(e.target)) e.preventDefault();
  };
  if (isDesktop) {
    return (
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side={side}
          aria-describedby={undefined}
          onInteractOutside={onInteractOutside}
          onOpenAutoFocus={e => e.preventDefault()}
          className="gap-0 p-0 w-auto border-foreground/10 block sm:max-w-none bg-background"
        >
          <div className={cn('p-5 h-full overflow-y-auto', className)}>{children}</div>
        </SheetContent>
      </Sheet>
    );
  }
  return (
    <Drawer open={open} onOpenChange={setOpen} repositionInputs={false}>
      <DrawerContent
        aria-describedby={undefined}
        onInteractOutside={onInteractOutside}
        onOpenAutoFocus={e => e.preventDefault()}
        className="w-full max-h-[calc(100%-52px)]"
      >
        <div className={cn('px-5 pb-5 h-full overflow-y-auto', className)}>
          {children}
          {/* Below the panel content, so it never overlaps the CTA. Mobile only (I2). */}
          {transactionMarket && <PanelTransactionActions owner={overlayOwner} />}
        </div>
      </DrawerContent>
    </Drawer>
  );
};
